import { createEmitter } from "../Utilities/events";
import { MediaType, detectMediaType, canPlayNativeHls } from "../Core/MediaTypes";
import { StreamingProviderEvents } from "./StreamingProvider";

/**
 * hls.js adaptive streaming provider.
 * Loaded lazily so the core stays free of hard SDK coupling at module parse time
 * when the package is absent (graceful degradation).
 */
export function createHlsJsProvider(options) {
  options = options || {};
  var emitter = createEmitter();
  var video = null;
  var hls = null;
  var HlsCtor = null;
  var name = "hls.js";
  var levels = [];
  var currentLevel = -1;
  var maxRetries = options.maxRetries != null ? options.maxRetries : 3;
  var retryCount = 0;

  function mapLevels(hlsLevels) {
    return (hlsLevels || []).map(function (lvl, index) {
      return {
        index: index,
        height: lvl.height || 0,
        width: lvl.width || 0,
        bitrate: lvl.bitrate || 0,
        name:
          (lvl.height ? lvl.height + "p" : "Level " + index) +
          (lvl.bitrate ? " · " + Math.round(lvl.bitrate / 1000) + "kbps" : ""),
        codec: lvl.videoCodec || "",
      };
    });
  }

  return {
    getName: function () {
      return name;
    },
    getSurfaceKind: function () {
      return "video";
    },
    initialize: function () {
      if (HlsCtor) {
        return Promise.resolve();
      }
      return import("hls.js")
        .then(function (mod) {
          HlsCtor = mod.default || mod;
          if (!HlsCtor || !HlsCtor.isSupported()) {
            return Promise.reject(
              new Error("hls.js is not supported in this browser")
            );
          }
        })
        .catch(function (err) {
          return Promise.reject({
            code: "HLSJS_UNAVAILABLE",
            message:
              (err && err.message) ||
              "hls.js could not be loaded. Install the hls.js dependency.",
            recoverable: false,
          });
        });
    },
    supports: function (source) {
      if (!source || !source.src) {
        return false;
      }
      var type = source.type || detectMediaType(source.src);
      // Prefer native on Safari; hls.js for others.
      if (type !== MediaType.HLS) {
        return false;
      }
      if (canPlayNativeHls() && !options.forceHlsJs) {
        return false;
      }
      return true;
    },
    attach: function (el) {
      video = el;
    },
    detach: function () {
      if (hls) {
        try {
          hls.detachMedia();
        } catch (e) {
          /* ignore */
        }
      }
      video = null;
    },
    load: function (source) {
      var self = this;
      return this.initialize().then(function () {
        if (!video) {
          return Promise.reject(new Error("hls.js: video not attached"));
        }
        if (hls) {
          try {
            hls.destroy();
          } catch (e) {
            /* ignore */
          }
          hls = null;
        }

        retryCount = 0;
        hls = new HlsCtor({
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 90,
          // OFF means OFF: do not auto-paint embedded HLS captions.
          // TrackController enables display only when the user selects a track.
          subtitleDisplay: false,
          enableWebVTT: true,
        });

        hls.on(HlsCtor.Events.MANIFEST_PARSED, function (event, data) {
          levels = mapLevels(data.levels || hls.levels);
          currentLevel = hls.currentLevel;
          emitter.emit(StreamingProviderEvents.MANIFEST_PARSED, {
            levels: levels,
            currentLevel: currentLevel,
          });
        });

        hls.on(HlsCtor.Events.LEVEL_SWITCHED, function (event, data) {
          currentLevel = data.level;
          emitter.emit(StreamingProviderEvents.LEVEL_SWITCHED, {
            level: currentLevel,
            levels: levels,
          });
        });

        hls.on(HlsCtor.Events.ERROR, function (event, data) {
          if (!data) {
            return;
          }
          if (data.fatal) {
            var recoverable =
              data.type === HlsCtor.ErrorTypes.NETWORK_ERROR ||
              data.type === HlsCtor.ErrorTypes.MEDIA_ERROR;

            if (recoverable && retryCount < maxRetries) {
              retryCount += 1;
              emitter.emit(StreamingProviderEvents.RETRY, {
                attempt: retryCount,
                maxRetries: maxRetries,
                type: data.type,
              });
              // Soft error for host diagnostics — engine keeps BUFFERING
              emitter.emit(StreamingProviderEvents.ERROR, {
                code: data.details || "HLSJS_FATAL",
                message: data.details || "Fatal HLS error",
                fatal: true,
                recoverable: true,
                raw: data,
              });
              if (data.type === HlsCtor.ErrorTypes.NETWORK_ERROR) {
                hls.startLoad();
              } else if (data.type === HlsCtor.ErrorTypes.MEDIA_ERROR) {
                hls.recoverMediaError();
              }
            } else {
              emitter.emit(StreamingProviderEvents.ERROR, {
                code: data.details || "HLSJS_FATAL",
                message: data.details || "Fatal HLS error",
                fatal: true,
                recoverable: false,
                raw: data,
              });
              try {
                hls.destroy();
              } catch (e) {
                /* ignore */
              }
              hls = null;
            }
          }
        });

        if (source.poster) {
          video.poster = source.poster;
        }

        hls.loadSource(source.src);
        hls.attachMedia(video);

        return new Promise(function (resolve, reject) {
          var settled = false;
          var onCanPlay = null;
          var onParsed = function () {
            if (settled) return;
            settled = true;
            cleanup();
            resolve();
          };
          var onError = function (err) {
            if (settled) return;
            if (err && err.fatal && !err.recoverable) {
              settled = true;
              cleanup();
              reject(err);
            }
          };
          var cleanup = function () {
            emitter.off(StreamingProviderEvents.MANIFEST_PARSED, onParsed);
            emitter.off(StreamingProviderEvents.ERROR, onError);
            if (onCanPlay && video) {
              video.removeEventListener("canplay", onCanPlay);
            }
          };
          emitter.on(StreamingProviderEvents.MANIFEST_PARSED, onParsed);
          emitter.on(StreamingProviderEvents.ERROR, onError);

          // Also resolve on canplay for short manifests
          onCanPlay = function () {
            if (settled) return;
            settled = true;
            cleanup();
            resolve();
          };
          video.addEventListener("canplay", onCanPlay);
        });
      });
    },
    getLevels: function () {
      return levels.slice();
    },
    setLevel: function (index) {
      if (!hls) {
        return;
      }
      // -1 = auto
      hls.currentLevel = index;
      currentLevel = index;
    },
    getCurrentLevel: function () {
      return currentLevel;
    },
    getAudioTracks: function () {
      if (!hls || !hls.audioTracks || !hls.audioTracks.length) {
        return [];
      }
      return hls.audioTracks.map(function (t, index) {
        return {
          id: "hls-" + index,
          index: index,
          label: t.name || t.lang || "Audio " + (index + 1),
          language: t.lang || "",
          kind: t.default ? "main" : "alternative",
          default: !!t.default,
        };
      });
    },
    setAudioTrack: function (index) {
      if (!hls || index == null) {
        return;
      }
      var n = typeof index === "string" ? parseInt(index, 10) : index;
      if (!isNaN(n)) {
        hls.audioTrack = n;
      }
    },
    getSubtitleTracks: function () {
      if (!hls || !hls.subtitleTracks || !hls.subtitleTracks.length) {
        return [];
      }
      return hls.subtitleTracks.map(function (t, index) {
        return {
          id: "hls-sub-" + index,
          index: index,
          label: t.name || t.lang || "Captions " + (index + 1),
          language: t.lang || "",
        };
      });
    },
    setSubtitleDisplay: function (enabled) {
      if (!hls) {
        return;
      }
      hls.subtitleDisplay = !!enabled;
    },
    setSubtitleTrack: function (index) {
      if (!hls) {
        return;
      }
      var n = typeof index === "string" ? parseInt(index, 10) : index;
      if (isNaN(n)) {
        n = -1;
      }
      hls.subtitleTrack = n;
    },
    on: emitter.on,
    off: emitter.off,
    destroy: function () {
      if (hls) {
        try {
          hls.destroy();
        } catch (e) {
          /* ignore */
        }
        hls = null;
      }
      levels = [];
      currentLevel = -1;
      video = null;
      emitter.emit(StreamingProviderEvents.DESTROYED);
      emitter.removeAllListeners();
    },
  };
}

export default createHlsJsProvider;
